'use client'

import BidIcon from '@/icons/bid'

import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { ArrowRight, Zap } from 'lucide-react'
import BitcoinIcon from '@/icons/bitcoin-filled'
import CollectionIcon from '@/components/collection-icon'
import { useCollectionOffers } from '@/utils/hooks/useOffers'
import useCollection from '@/utils/hooks/useCollection'
import useEscrows from '@/utils/hooks/useEscrows'
import useIsHydrated from '@/utils/hooks/useIsHydrated'
import useLoggedIn from '@/utils/hooks/useLoggedIn'
import { offersEnabledFor, type Offer } from '@/utils/offers'
import { instantSellOffers } from '@/utils/instant-sell'
import { bidAnchors } from '@/utils/market-tile'
import useBidReference from '@/utils/hooks/useBidReference'
import Wallet from '@/utils/wallet'
import { InstantSellDialog } from './instant-sell'
import MakeOfferModal from './make-offer-modal'
import CancelOfferModal from './cancel-offer-modal'
import OfferUsd from './offer-usd'
import { bookBtc } from './collection-offer-book'
import styles from './market-tile.module.css'
import inscriptionStyles from '@/components/inscription-card/InscriptionCard.module.css'

/** Chip figure: three significant digits, so a sub-1k-sat bid still fits a 60px chip. */
const chipBtc = (sats: number) => {
	const btc = sats / 1e8
	const digits = Math.max(2, 2 - Math.floor(Math.log10(btc)))
	return btc.toLocaleString('en-US', { maximumFractionDigits: Math.min(8, digits) })
}

/** The figure scales to its own length: one line, never split mid-number. */
const figureFit = (text: string) => ({ '--chars': text.length + 1.7 } as CSSProperties)

/**
 * The first cell of a collection grid. With a bid in the book it sells into
 * it; with none it is the shortest path to the first bid. One dominant figure
 * per state: the top bid, or the floor a bid would be measured against.
 */
export default function MarketTile({ slug, name }: { slug: string; name?: string | null }) {
	const hydrated = useIsHydrated()
	const enabled = hydrated && offersEnabledFor(slug)
	const loggedIn = useLoggedIn()
	const me = loggedIn ? Wallet.getTaprootAddress() : ''
	const { data, error, isLoading, mutate } = useCollectionOffers(enabled ? slug : null)
	const { data: collection } = useCollection(enabled ? slug : '')
	const { data: listings } = useEscrows(enabled ? slug : null)
	const [selling, setSelling] = useState(false)
	const [bidAt, setBidAt] = useState<number | null>(null)
	// The bid an update replaces: once the new one is live, this one is
	// refunded so the buyer never holds two bids on the same collection.
	const [replacing, setReplacing] = useState<Offer | null>(null)
	const [refunding, setRefunding] = useState<Offer | null>(null)
	const [now, setNow] = useState(Date.now)
	useEffect(() => {
		if (!enabled) return
		const timer = setInterval(() => setNow(Date.now()), 5_000)
		return () => clearInterval(timer)
	}, [enabled])

	const floorSats = useMemo(() => {
		if (!Array.isArray(listings)) return null
		const prices = listings
			.map((l: any) => Number(l?.satoshi_price))
			.filter((p) => Number.isFinite(p) && p > 0)
		return prices.length ? Math.min(...prices) : null
	}, [listings])

	// Bids anchor to the fair-value mark, the floor only while no mark exists.
	const bidRef = useBidReference(enabled ? slug : null, floorSats)
	const anchorSats = bidRef.sats
	const anchorLabel = bidRef.label
	if (!enabled) return null

	// A refresh error keeps the last-known book (SWR retains `data`); only a
	// book that never loaded is "unavailable".
	const all: Offer[] = instantSellOffers(data, slug, now)
	const unavailable = !!error && !data
	const isMine = (o: Offer) => !!me && (o.buyer_address === me || o.buyer_payment_address === me)
	const sellable = all.filter((o) => !isMine(o))
	const byPrice = (a: Offer, b: Offer) => b.price_sats - a.price_sats
	const top =
		[...sellable].filter((o) => o.scope === 'collection').sort(byPrice)[0] ??
		[...sellable].sort(byPrice)[0]
	const myTop = all.filter(isMine).sort(byPrice)[0]

	const openBid = (sats: number | null) => setBidAt(sats && sats > 0 ? sats : anchorSats ?? 0)

	const modals = (
		<>
			{selling && (
				<InstantSellDialog
					slug={slug}
					name={name}
					offers={sellable}
					onClose={() => setSelling(false)}
				/>
			)}
			{bidAt !== null && (
				<MakeOfferModal
					open
					onClose={() => {
						setBidAt(null)
						setReplacing(null)
					}}
					collectionSlug={slug}
					initialScope="collection"
					initialPriceSats={bidAt || undefined}
					traitOffers={all.filter((o) => o.scope === 'trait')}
					supply={Number(collection?.total_supply) || undefined}
					collectionIcon={collection ? <CollectionIcon collection={collection} /> : undefined}
					name={name || slug}
					reference={{ floorSats, fairSats: bidRef.fairSats, topOfferSats: top?.price_sats ?? null }}
					onPlaced={() => {
						setBidAt(null)
						void mutate()
						if (replacing) {
							setRefunding(replacing)
							setReplacing(null)
						}
					}}
				/>
			)}
			{refunding && (
				<CancelOfferModal
					offer={refunding}
					onClose={() => setRefunding(null)}
					onChanged={() => void mutate()}
				/>
			)}
		</>
	)

	if (isLoading && !data)
		return (
			<section className={`${styles.tile} ${inscriptionStyles.cardShell}`} aria-busy="true" aria-label="Collection offers loading">
				<span className={styles.skeletonShort} />
				<span className={styles.skeletonFigure} />
				<span className={styles.skeletonShort} />
				<span className={styles.skeletonButton} />
			</section>
		)

	if (top)
		return (
			<>
				<div className={`${styles.sellCard} ${inscriptionStyles.cardShell}`} data-testid="market-tile-sell-card">
					<svg className={styles.sellOutline} aria-hidden="true"><rect x="2" y="2" rx="16" /></svg>
					<div className={styles.sellBody}>
						<button
							type="button"
							className={styles.sellContent}
							aria-label={`Instant sell ${name || slug} into the top offer`}
							data-testid="market-tile-sell"
							onClick={() => {
								void mutate()
								setSelling(true)
							}}
						>
							<span className={styles.sellGlyph}><Zap aria-hidden="true" /></span>
							<span className={styles.sellTitle}>Instant Sell</span>
							<span className={styles.sellLabel}>Top offer</span>
							<span className={styles.sellPrice}>
								<BitcoinIcon />{bookBtc(top.price_sats)}
							</span>
							<span className={styles.sellUsd}><OfferUsd sats={top.price_sats} compact /></span>
						</button>
						<button
							type="button"
							className={styles.sellViewAll}
							onClick={() => {
								void mutate()
								setSelling(true)
							}}
						>
							View {sellable.length} {sellable.length === 1 ? 'offer' : 'offers'}
							<ArrowRight aria-hidden="true" />
						</button>
					</div>
					{/* Stacked full-width, inset from the card's outline. */}
					<div className={styles.sellActions}>
						<button
							type="button"
							className={`${styles.sellAction} ${styles.sellNow}`}
							onClick={() => {
								void mutate()
								setSelling(true)
							}}
						>
							<Zap aria-hidden="true" />
							<span className={styles.labelLong}>Sell now</span>
							<span className={styles.labelShort}>Sell</span>
						</button>
						<button
							type="button"
							className={`${styles.sellAction} ${styles.sellOffer}`}
							data-testid="market-tile-bid"
							aria-label={`Make an offer on ${name || slug}`}
							onClick={() => setBidAt(0)}
						>
							<BidIcon size={13} aria-hidden="true" />
							<span className={styles.labelLong}>Make offer</span>
							<span className={styles.labelShort}>Offer</span>
						</button>
					</div>
				</div>
				{modals}
			</>
		)

	const anchors = unavailable ? [] : bidAnchors(anchorSats)
	const headline = unavailable ? 'Offers unavailable' : myTop ? 'Your offer leads' : 'No offers yet'
	return (
		<section
			className={`${styles.tile} ${inscriptionStyles.cardShell}`}
			data-state={unavailable ? 'unavailable' : 'empty'}
			aria-label={
				unavailable
					? `Offers unavailable for ${name || slug}`
					: myTop
					? `Your offer leads on ${name || slug}`
					: `Make an offer on ${name || slug}`
			}
		>
			<h3 className={styles.kicker}>
				<span className={styles.glyph}>
					<BidIcon size={16} aria-hidden="true" />
				</span>
				{headline}
			</h3>
			<div className={styles.body}>
				<span className={styles.label}>{myTop ? 'Your offer' : anchorLabel}</span>
				<strong
					className={styles.figure}
					style={figureFit(
						myTop ? bookBtc(myTop.price_sats) : anchorSats ? bookBtc(anchorSats) : '—'
					)}
				>
					{myTop ? bookBtc(myTop.price_sats) : anchorSats ? bookBtc(anchorSats) : '—'}
					{(myTop || anchorSats) && <BitcoinIcon className={styles.btc} />}
				</strong>
				<span className={styles.meta}>
					{myTop ? (
						<OfferUsd sats={myTop.price_sats} block />
					) : anchorSats ? (
						<OfferUsd sats={anchorSats} block />
					) : (
						'No listings or sales to anchor to'
					)}
				</span>
				{unavailable ? (
					<span className={styles.meta} role="status">
						We couldn’t load the book.{' '}
						<button type="button" className={styles.retry} onClick={() => void mutate()}>
							Retry
						</button>
					</span>
				) : (
					(myTop ? (
						<span className={styles.meta}>
							Updating makes a new offer, then asks you to sign a refund of this one.
						</span>
					) : (
						<span className={`${styles.meta} ${styles.finePrint}`}>
							Be first. Your BTC waits in escrow until a seller accepts or you cancel.
						</span>
					))
				)}
			</div>
			{!myTop && anchors.length > 0 && (
				<div className={styles.anchors} role="group" aria-label={`Start from a price below ${anchorLabel.toLowerCase()}`}>
					{anchors.map((a) => (
						<button
							key={a.label}
							type="button"
							className={styles.anchor}
							onClick={() => openBid(a.sats)}
							aria-label={`Offer ${a.label} of ${anchorLabel.toLowerCase()}, ${bookBtc(a.sats)} BTC`}
						>
							<span>{a.label}</span>
							<small>{chipBtc(a.sats)}</small>
						</button>
					))}
				</div>
			)}
			<div className={styles.actions}>
				<button
					type="button"
					className={styles.primary} data-place-bid-button
					data-testid="market-tile-bid"
					// The plain button opens the form at its own default; only a chip
					// pre-fills a price, and the chip shows that price first.
					onClick={() => {
						if (myTop) {
							setReplacing(myTop)
							openBid(myTop.price_sats)
						} else setBidAt(0)
					}}
				>
					{myTop ? 'Update offer' : 'Make offer'}
				</button>
			</div>
			{modals}
		</section>
	)
}
